/**
 * WS-RAIL — the page panel.
 *
 * The second half of the split chrome: the rail says which workspace, this
 * says which page. It is the part collapsing hides, and hiding it is now safe
 * precisely because the rail survives — you keep both the workspace switcher
 * and a route back to Home at 64px.
 *
 * It carries the workspace name as its own header. That name used to live in
 * the switcher control, which had to be a button because it also opened the
 * flyout; now that switching happens in the rail, the name can be what it
 * always wanted to be — a heading over the list it labels.
 */
import type { ReactElement } from "react";
import { Box } from "@mui/material";
import { typography } from "@aeriosiq/ui";
import { useActiveWorkspace } from "../workspaces/useWorkspaces.js";
import { WorkspaceNav } from "./WorkspaceNav.js";
import { DocumentationRow } from "./AppSidebar.js";
import { PANEL_WIDTH, RAIL_HEADER_H } from "./primitives.js";

export function WorkspacePanel(): ReactElement {
  const { workspace } = useActiveWorkspace();

  return (
    <Box
      sx={{
        width: PANEL_WIDTH,
        flexShrink: 0,
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        minWidth: 0,
      }}
    >
      {/* Same header height as the rail, so the mark and the workspace name
          sit on one baseline. Everything in this column — heading, section
          captions, row text — is on the SAME 20px column. The old chrome had
          the brand at 16, the rows at 24 and the captions at 16: three
          columns in one list, which is what "displaced" and "messy" were
          actually describing. */}
      <Box
        sx={{
          height: RAIL_HEADER_H,
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          // 20px — the column the rows and captions use. See WorkspaceNav.
          px: 2.5,
          minWidth: 0,
        }}
      >
        {/* Deliberately NOT a heading. The list below already carries
            `aria-label="<Workspace> navigation"`, so a heading here repeats
            it — and because a workspace often shares its name with a page
            (Projects → /projects), an <h2> also puts a second "Projects" in
            the heading outline next to the page's own <h1>. Plain text is
            read by a screen reader all the same and competes with nothing. */}
        <Box
          component="div"
          sx={{
            m: 0,
            fontSize: typography.size["body-md"],
            fontWeight: typography.weight.medium,
            letterSpacing: typography.tracking["body-md"],
            color: "text.primary",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {workspace?.label ?? " "}
        </Box>
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        <WorkspaceNav />
      </Box>

      <Box component="ul" sx={{ m: 0, p: 0, listStyle: "none", pb: 1 }}>
        <DocumentationRow />
      </Box>
    </Box>
  );
}
